import { createSelector } from 'reselect'
import dayjs from 'dayjs'
import _ from 'lodash'

import {
  getConsentForms,
  getResidentsList,
  getUsersList,
  getLogCategories,
  getLogCategoryItems,
  getConsentFormTemplates,
} from './Lists'
import { ConsentFormTemplate } from '@lmc/common/types'
import { shortCutBuilder } from '../screens/Residents/screens/ResidentConsentScreens/utils'
import { Carer, ConsentForm, Log } from '@lmc/ts-common'

const getState = (form: {
  isArchived: any
  isRefused: any
  signature: any
}) => {
  const { isArchived, isRefused, signature } = form

  if (isArchived) {
    return 'archived'
  }

  if (isRefused) {
    return 'refused'
  }

  if (signature) {
    return 'signed'
  }

  return 'unsigned'
}

// Generates the list item sub title using the form data
const getSubTitle = (form: {
  editedBy?: any
  editedOn?: any
  createdOn?: any
  createdBy?: any
  isArchived?: any
  isRefused?: any
  signature?: any
}) => {
  const editedBy = form?.editedBy
  const editedOn = form?.editedOn
  const createdOn = form?.createdOn
  const createdBy = form?.createdBy

  // @ts-ignore
  switch (getState(form)) {
    case 'archived':
      return `Archived on ${dayjs(editedOn).format(
        `${'DD-MM-YYYY • HH:mm'}`
      )} • ${editedBy?.name?.first} ${editedBy?.name?.last}`
    case 'refused':
      return `Refused on ${dayjs(editedOn).format(
        `${'DD-MM-YYYY • HH:mm'}`
      )} • ${editedBy?.name?.first} ${editedBy?.name?.last}`
    case 'signed':
      return `Signed on ${dayjs(editedOn || createdOn).format(
        `${'DD-MM-YYYY • HH:mm'}`
      )} • ${(editedBy || createdBy)?.name?.first} ${
        (editedBy || createdBy)?.name?.last
      }`
    default:
      return `Added on ${dayjs(createdOn).format(
        `${'DD-MM-YYYY • HH:mm'}`
      )} • ${createdBy?.name?.first} ${createdBy?.name?.last}`
  }
}

// Takes the consent forms and formats them for the list items
const formatFormsToListItems = (forms: any[]) =>
  forms.map((form) => ({
    titleContent: form.title,
    subTitleContent: getSubTitle(form),
    iconContent: shortCutBuilder(form.title),
    state: getState(form),
    id: form.id,
    ...form,
  }))

export const getResidentConsentForms = createSelector(
  [
    getConsentForms,
    getResidentsList,
    getUsersList,
    getLogCategories,
    getLogCategoryItems,
  ],
  (
    residentConsentForms: ConsentForm[],
    residents,
    users,
    logCategories,
    logCategoryItems
  ) => {
    if (
      !residentConsentForms ||
      !residents ||
      !users ||
      !logCategories ||
      !logCategoryItems
    ) {
      return null
    }
    const populatedConsentForms = _(residentConsentForms)
      .filter((form) => !!form)
      .map((consentForm) => ({
        ...consentForm,
        createdBy: _.find(users, { id: consentForm.createdBy }),
        editedBy: _.find(users, { id: consentForm.editedBy }),
        logs: _(consentForm?.logs)
          .sortBy((log: Log) => dayjs(log?.timeLogged).toDate(), 'desc')
          .map((log) => ({
            ...log,
            loggedBy: _.find(users, { id: log?.loggedBy }),
            editedBy: _.find(users, { id: log?.editedBy }),
            resident: _.find(residents, { id: log?.resident }),
            category: _.find(logCategories, { id: log?.category }),
            item: _.find(logCategoryItems, { id: log?.item }),
            witnessedBy: _.find(users, { id: log?.witnessedBy }),
          }))
          .value(),
      }))
      .sortBy(['isArchived', 'type', 'title'])
      .value()

    return formatFormsToListItems(populatedConsentForms)
  }
)

// Consent Form Templates Library

const getTemplateSummarySubtitle = (template: {
  isArchived: any
  createdOn: any
  createdBy: any
  editedOn: any
  editedBy: any
}) => {
  const { isArchived, createdOn, createdBy, editedOn, editedBy } = template

  if (!isArchived) return ``

  return `${isArchived ? 'Archived ' : 'Created'}
        by ${
          isArchived
            ? `${editedBy?.name?.first} ${editedBy?.name?.last}`
            : `${createdBy?.name?.first} ${createdBy?.name?.last}`
        }
        at ${
          isArchived
            ? `${dayjs(editedOn).format('DD-MM-YYYY • HH:mm')}`
            : `${dayjs(createdOn).format('DD-MM-YYYY • HH:mm')}`
        }
    `
}

const getTemplateSummary = (template: {
  createdBy?: any
  editedBy?: any
  id?: any
  home?: string
  title?: any
  body?: string
  isArchived: any
  isMaster?: boolean
  editedOn?: any
  createdOn?: any
  type?: any
  isCreated?: boolean | undefined
}) => {
  const { title, id, isArchived, type } = template
  return {
    titleContent: title,
    state: isArchived ? 'archived' : 'default',
    // @ts-ignore
    subTitleContent: getTemplateSummarySubtitle(template),
    iconContent: shortCutBuilder(title),
    type,
    id,
  }
}

export const getHomeTemplates = createSelector(
  [getConsentFormTemplates, getUsersList],
  (consentFormTemplates: ConsentFormTemplate[], users: Carer[]) => {
    if (!consentFormTemplates || !users) return []

    return _(consentFormTemplates)
      .sortBy(['isArchived', 'type', 'title'])
      .map((template) => ({
        ...template,
        createdBy: _.find(users, { id: template.createdBy }),
        editedBy: _.find(users, { id: template.editedBy }),
      }))
      .map((template) => getTemplateSummary(template))
      .value()
  }
)
